import { useAuiState } from '@assistant-ui/react';
import { useDeferredValue } from 'react';
import type { z } from 'zod';

/**
 * Options for useToolPart hook with optional schema validation.
 */
interface UseToolPartOptions<ArgsType, ResultType> {
  /** Optional Zod schema for validating args. When provided, invalid args return undefined. */
  argsSchema?: z.ZodType<ArgsType>;
  /** Optional Zod schema for validating result. When provided, invalid result returns undefined. */
  resultSchema?: z.ZodType<ResultType>;
}

/**
 * Hook for accessing tool call arguments and results from assistant-ui state.
 *
 * Streamed `args` and `result` are wrapped with {@link useDeferredValue} so
 * consumers (display chrome, custom render components, the Show-details
 * panel) get a concurrent-aware deferred view during high-frequency
 * streaming. Unlike a setTimeout-based throttle, `useDeferredValue`
 * respects React's commit cycle — it never schedules an update after the
 * part context has been invalidated (e.g. thread switch mid-stream),
 * which would otherwise crash inside the assistant-ui part proxy.
 *
 * Tool name is not deferred — it's set once when the tool call starts.
 *
 * When schemas are provided, validates data and returns undefined for invalid values.
 * When schemas are not provided, returns raw data typed as the generic parameter
 * (caller accepts responsibility for type correctness).
 *
 * @example With schema validation (recommended):
 * ```tsx
 * const { args, result } = useToolPart({
 *   argsSchema: MyArgsSchema,
 *   resultSchema: MyResultSchema,
 * });
 * ```
 *
 * @example Without schemas (caller accepts type responsibility):
 * ```tsx
 * const { args, result } = useToolPart<MyArgs, MyResult>();
 * ```
 */
export function useToolPart<ArgsType = unknown, ResultType = unknown>(
  options?: UseToolPartOptions<ArgsType, ResultType>,
) {
  const argsRaw = useAuiState((state) => (state.part.type === 'tool-call' ? state.part.args : undefined));
  const resultRaw = useAuiState((state) => (state.part.type === 'tool-call' ? state.part.result : undefined));
  const toolName = useAuiState((state) => (state.part.type === 'tool-call' ? state.part.toolName : undefined));

  // Defer args/result so every consumer downstream of useToolPart
  // (display chrome, content slots, Show-details, custom renders) gets
  // the same deferred view automatically. React handles the scheduling;
  // no setTimeout, no stale renders after the part context unmounts.
  const argsDeferred = useDeferredValue(argsRaw);
  const resultDeferred = useDeferredValue(resultRaw);

  // const args = parseWithSchema(argsDeferred, options?.argsSchema);
  // const result = parseWithSchema(resultDeferred, options?.resultSchema);
  const args = parseWithSchema(argsDeferred, options?.argsSchema);
  const result = parseWithSchema(resultDeferred, options?.resultSchema);

  return { args, result, toolName } as const;
}

// Parse with schema validation, or return raw value with type cast
function parseWithSchema<T>(raw: unknown, schema?: z.ZodType<T>): T | undefined {
  if (!schema) {
    // Without schema: return raw value typed as T (caller accepts type responsibility)
    // biome-ignore lint/suspicious/noExplicitAny: Callers specify type via generic parameter
    return raw as any;
  }
  const result = schema.safeParse(raw);
  return result.success ? result.data : undefined;
}
